import { NotificationData } from "@/api/definitions";

// Notification rendering metadata shared between the notifications page
// (NotificationCard) and the top bar dropdown (NotificationsDropdown),
// so titles, messages, icons and accents cannot drift between the two views.

export interface NotificationContent {
    title: string;
    message: string;
    icon: string;
    accent: string;

    /**
     * True if the notification is caused by an actor (participant or user)
     * and the message expects the actor as the sentence subject.
     */
    hasActor: boolean;

    /**
     * True if the notification carries a reason (terminations and rejections).
     */
    hasReason: boolean;

    /**
     * True if the notification message expects a dataset label.
     */
    hasDataset: boolean;
}

export function getNotificationDatasetLabel(data: NotificationData, $t: (t: string) => string): string {
    return data.datasetTitle || data.datasetId || $t("Unknown dataset");
}

export function getNotificationContent(type: string, $t: (t: string) => string): NotificationContent {
    switch (type) {
        case "negotiation:request":
            return {
                title: $t("New negotiation request"),
                message: $t("made a negotiation request for the dataset"),
                icon: "fa-solid fa-hand-holding-dollar",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:offer":
            return {
                title: $t("New negotiation offer"),
                message: $t("made a negotiation offer for the dataset"),
                icon: "fas fa-file-signature",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:accept":
            return {
                title: $t("Accepted negotiation offer"),
                message: $t("accepted your negotiation offer for the dataset"),
                icon: "fas fa-circle-check",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:agree":
            return {
                title: $t("Agreement created"),
                message: $t("created an agreement for the dataset"),
                icon: "fas fa-handshake-simple",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:verify":
            return {
                title: $t("Agreement verified"),
                message: $t("verified an agreement for the dataset"),
                icon: "fas fa-shield-halved",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:finalize":
            return {
                title: $t("Negotiation finalized"),
                message: $t("finalized a negotiation for the dataset"),
                icon: "fas fa-flag-checkered",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:terminate":
            return {
                title: $t("Negotiation terminated"),
                message: $t("terminated a negotiation for the dataset"),
                icon: "fas fa-circle-xmark",
                accent: "danger",
                hasActor: true,
                hasReason: true,
                hasDataset: true,
            };
        case "agreement:dispute:new_messages":
            return {
                title: $t("New dispute messages for an agreement"),
                message: $t("There are new dispute messages for an agreement."),
                icon: "fas fa-comments",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "agreement:dispute:lock":
            return {
                title: $t("Dispute was locked"),
                message: $t("The clearing house locked the dispute. No new messages are allowed."),
                icon: "fas fa-lock",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "agreement:dispute:unlock":
            return {
                title: $t("Dispute was unlocked"),
                message: $t("The clearing house unlocked the dispute. New messages may be sent by the participants."),
                icon: "fas fa-unlock",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "provider:request:received":
            return {
                title: $t("Received provider registration request"),
                message: $t("made a provider registration request"),
                icon: "fas fa-user-plus",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };
        case "broker:request:accepted":
            return {
                title: $t("Accepted broker registration request"),
                message: $t("The metadata broker accepted the request."),
                icon: "fas fa-circle-check",
                accent: "success",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "broker:request:rejected":
            return {
                title: $t("Rejected broker registration request"),
                message: $t("The metadata broker rejected the request."),
                icon: "fas fa-circle-xmark",
                accent: "danger",
                hasActor: false,
                hasReason: true,
                hasDataset: false,
            };
        case "attestation:request:received":
            return {
                title: $t("Received attestation issuance request"),
                message: $t("made an attestation issuance request"),
                icon: "fas fa-stamp",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };
        case "attestator:response:accepted":
            return {
                title: $t("Accepted attestation issuance request"),
                message: $t("The attestator accepted the request."),
                icon: "fas fa-circle-check",
                accent: "success",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "attestator:response:rejected":
            return {
                title: $t("Rejected attestation issuance request"),
                message: $t("The attestator rejected the request."),
                icon: "fas fa-circle-xmark",
                accent: "danger",
                hasActor: false,
                hasReason: true,
                hasDataset: false,
            };
        default:
            return {
                title: $t("Unknown notification"),
                message: $t("Unknown notification"),
                icon: "fas fa-bell",
                accent: "neutral",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
    }
}
